import { Box, Divider, Flex, Switch, Text, VStack } from '@chakra-ui/react';
import type { ReactNode } from 'react';

import { type Role, Tab, type TrialType } from '@revelio/data-access';

import {
  formatExpirationDate,
  formatValueLabel,
} from './impersonationBanner.utils';

interface ImpersonationUserTooltipContentProps {
  resolvedTrialUser: boolean | undefined;
  resolvedTrialType: TrialType | undefined;
  resolvedExpirationDate: string | undefined;
  resolvedRole: Role | undefined;
}

export function ImpersonationUserTooltipContent({
  resolvedTrialUser,
  resolvedTrialType,
  resolvedExpirationDate,
  resolvedRole,
}: ImpersonationUserTooltipContentProps) {
  const rows: ReactNode[] = [];

  if (resolvedTrialUser !== undefined) {
    rows.push(
      <Flex
        key="trial-user"
        alignItems="center"
        justifyContent="space-between"
        gap={3}
      >
        <Text fontSize="xs" fontWeight="semibold" color="navyBlue.500">
          Trial User
        </Text>
        <Flex alignItems="center" gap={2}>
          <Text fontSize="xs" color="navyBlue.500">
            Off
          </Text>
          <Switch
            size="sm"
            isChecked={resolvedTrialUser}
            isDisabled={true}
            colorScheme="green"
            opacity={1}
          />
          <Text fontSize="xs" color="navyBlue.500">
            On
          </Text>
        </Flex>
      </Flex>
    );
  }

  if (resolvedTrialType) {
    rows.push(
      <Flex
        key="trial-type"
        alignItems="center"
        justifyContent="space-between"
        gap={3}
      >
        <Text fontSize="xs" fontWeight="semibold" color="navyBlue.500">
          Trial Type
        </Text>
        <Text fontSize="xs" fontWeight="semibold" color="navyBlue.500">
          {formatValueLabel(String(resolvedTrialType))}
        </Text>
      </Flex>
    );
  }

  if (resolvedExpirationDate) {
    rows.push(
      <Flex
        key="trial-ends"
        alignItems="center"
        justifyContent="space-between"
        gap={3}
      >
        <Text fontSize="xs" fontWeight="semibold" color="navyBlue.500">
          Trial Ends
        </Text>
        <Text fontSize="xs" color="navyBlue.500" fontWeight="semibold">
          {formatExpirationDate(resolvedExpirationDate)}
        </Text>
      </Flex>
    );
  }

  if (resolvedRole) {
    rows.push(
      <Flex
        key="role"
        alignItems="center"
        justifyContent="space-between"
        gap={3}
      >
        <Text fontSize="xs" fontWeight="semibold" color="navyBlue.500">
          Role
        </Text>
        <Text fontSize="xs" fontWeight="semibold" color="navyBlue.500">
          {formatValueLabel(String(resolvedRole))}
        </Text>
      </Flex>
    );
  }

  if (rows.length === 0) {
    return (
      <Text fontSize="xs" color="navyBlue.500">
        User settings unavailable
      </Text>
    );
  }

  return (
    <VStack align="stretch" spacing={2}>
      {rows}
    </VStack>
  );
}

interface ImpersonationClientTooltipContentProps {
  resolvedClientName: string | undefined;
  resolvedTabs: Tab[] | undefined;
  resolvedLinkupPostings: boolean | undefined;
  resolvedReportTypes: string[];
  resolvedDatasetLabels: string[];
}

export function ImpersonationClientTooltipContent({
  resolvedClientName,
  resolvedTabs,
  resolvedLinkupPostings,
  resolvedReportTypes,
  resolvedDatasetLabels,
}: ImpersonationClientTooltipContentProps) {
  const rows: ReactNode[] = [];
  const sections: ReactNode[] = [];
  const hasReportsTab = resolvedTabs?.includes(Tab.Reports) ?? false;
  const hasDeliverablesTab = resolvedTabs?.includes(Tab.Deliverables) ?? false;

  if (resolvedClientName) {
    rows.push(
      <Flex
        key="client"
        alignItems="center"
        justifyContent="space-between"
        gap={3}
      >
        <Text fontSize="xs" fontWeight="semibold" color="navyBlue.500">
          Client Group
        </Text>
        <Text fontSize="xs" fontWeight="semibold" color="navyBlue.500">
          {resolvedClientName}
        </Text>
      </Flex>
    );
  }

  if (resolvedLinkupPostings !== undefined) {
    rows.push(
      <Flex
        key="linkup-postings"
        alignItems="center"
        justifyContent="space-between"
        gap={3}
      >
        <Text fontSize="xs" fontWeight="semibold" color="navyBlue.500">
          Website Postings
        </Text>
        <Flex alignItems="center" gap={2}>
          <Text fontSize="xs" color="navyBlue.500">
            Off
          </Text>
          <Switch
            size="sm"
            isChecked={Boolean(resolvedLinkupPostings)}
            isDisabled={true}
            colorScheme="green"
            opacity={1}
          />
          <Text fontSize="xs" color="navyBlue.500">
            On
          </Text>
        </Flex>
      </Flex>
    );
  }

  if (resolvedTabs && resolvedTabs.length > 0) {
    rows.push(
      <Flex
        key="tabs"
        alignItems="flex-start"
        justifyContent="space-between"
        gap={3}
      >
        <Text fontSize="xs" fontWeight="semibold" color="navyBlue.500" mt={0.5}>
          Tabs
        </Text>
        <Flex wrap="wrap" justifyContent="flex-end" gap={1} maxW="660px">
          {resolvedTabs.map((tab, index) => (
            <Box
              key={`${tab}-${index}`}
              px={2}
              py={0.5}
              borderRadius="md"
              bg="blue.100"
              border="1px solid"
              borderColor="blue.300"
            >
              <Text fontSize="xs" fontWeight="semibold" color="blue.800">
                {formatValueLabel(String(tab))}
              </Text>
            </Box>
          ))}
        </Flex>
      </Flex>
    );
  }

  if (rows.length > 0) {
    sections.push(
      <VStack key="client-group" align="stretch" spacing={2}>
        {rows}
      </VStack>
    );
  }

  if (hasReportsTab && resolvedReportTypes.length > 0) {
    sections.push(
      <Flex
        key="report-types"
        alignItems="flex-start"
        justifyContent="space-between"
        gap={3}
      >
        <Text fontSize="xs" fontWeight="semibold" color="navyBlue.500" mt={0.5}>
          Report Types
        </Text>
        <Flex wrap="wrap" justifyContent="flex-end" gap={1} maxW="660px">
          {resolvedReportTypes.map((reportType, index) => (
            <Box
              key={`${reportType}-${index}`}
              px={2}
              py={0.5}
              borderRadius="md"
              bg="blue.100"
              border="1px solid"
              borderColor="blue.300"
            >
              <Text fontSize="xs" fontWeight="semibold" color="blue.800">
                {reportType}
              </Text>
            </Box>
          ))}
        </Flex>
      </Flex>
    );
  }

  if (hasDeliverablesTab && resolvedDatasetLabels.length > 0) {
    sections.push(
      <Flex
        key="datasets"
        alignItems="flex-start"
        justifyContent="space-between"
        gap={3}
      >
        <Text fontSize="xs" fontWeight="semibold" color="navyBlue.500" mt={0.5}>
          Subscribed Datasets
        </Text>
        <Flex wrap="wrap" justifyContent="flex-end" gap={1} maxW="660px">
          {resolvedDatasetLabels.map((dataset, index) => (
            <Box
              key={`${dataset}-${index}`}
              px={2}
              py={0.5}
              borderRadius="md"
              bg="blue.100"
              border="1px solid"
              borderColor="blue.300"
            >
              <Text fontSize="xs" fontWeight="semibold" color="blue.800">
                {dataset}
              </Text>
            </Box>
          ))}
        </Flex>
      </Flex>
    );
  }

  if (sections.length === 0) {
    return (
      <Text fontSize="xs" color="navyBlue.500">
        Client group settings unavailable
      </Text>
    );
  }

  return (
    <VStack
      align="stretch"
      spacing={3}
      divider={<Divider borderColor="gray.200" />}
    >
      {sections}
    </VStack>
  );
}
